import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Cell,
  Legend,
} from "recharts";

const TopTagsChart = ({ stats, title = "Top 5 tags (by total issues)" }) => {
  // Prefer root: top_labels; fallback to repository_info.top_labels; then issues.tag_stats
  const fromApi =
    (stats?.top_labels || stats?.repository_info?.top_labels || [])
      .map(t => ({ name: t.name, count: t.issues || 0 }))
      .filter(d => d.count > 0);

  let fromFallback = [];
  const tagStats = stats?.issues?.tag_stats || {};
  if (!fromApi.length && tagStats && typeof tagStats === "object") {
    fromFallback = Object.entries(tagStats).map(([name, obj]) => ({
      name,
      count: (obj?.open || 0) + (obj?.closed || 0),
    })).filter(d => d.count > 0);
  }

  const data = (fromApi.length ? fromApi : fromFallback)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Use empty array for chart when no data, instead of text message
  const chartData = data.length ? data : [];

  // rank-based gradient pairs
  const getGradientPair = (rank) => {
    if (rank >= 5) return ["#25B9D5", "#64C8DF"];
    if (rank === 4) return ["#F6A42B", "#F7B860"];
    if (rank === 3) return ["#F04E4D", "#F6817E"];
    if (rank === 2) return ["#9165F6", "#AB8AF9"];
    return ["#10b981", "#14eba3"];
  };

  // Chart height grows with number of bars, but set minimum for empty chart
  const chartHeight = Math.max(300, data.length ? data.length * 80 : 300);

  return (
    <div className="card p-3 shadow-sm h-100">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0">{title}</h5>
      </div>

      <div style={{ width: "100%", height: chartHeight }}>
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 30, left: 0, bottom: 20 }}
            barCategoryGap="10%"
          >
            <defs>
              {chartData.map((_, i) => {
                const [from, to] = getGradientPair(chartData.length - i);
                return (
                  <linearGradient id={`tag-v-grad-${i}`} key={i} x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor={from} stopOpacity={1} />
                    <stop offset="30%" stopColor={to} stopOpacity={1} />
                    <stop offset="100%" stopColor={to} stopOpacity={0.4} />
                  </linearGradient>
                );
              })}
            </defs>

            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} angle={-20} textAnchor="end" height={50} />
            <YAxis allowDecimals={false} />
            <Tooltip
              contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc", borderRadius: 8 }}
              formatter={(v) => [v, "Issues"]}
            />

            <Bar
              dataKey="count"
              fillOpacity={1}
              radius={[8, 8, 0, 0]}
              isAnimationActive={true}
              label={{ position: "top", fill: "#333" }}
            >
              {chartData.map((_, i) => (
                <Cell key={`cell-${i}`} fill={`url(#tag-v-grad-${i})`} />
              ))}
              <LabelList dataKey="count" position="top" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TopTagsChart;